import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { componentWithName } from '@tds/util-prop-types';
import Box from '@tds/core-box';
import { list } from '@tds/shared-styles';
import { safeRest } from '@tds/util-helpers';
import { small, smallFont, medium, mediumFont, large, largeFont } from '@tds/shared-typography';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends() {
  module.exports = _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

module.exports = _extends;
});

// colours
var colorTelusPurple = '#4b286d';
var colorAccessibleGreen = '#2B8000';
var colorCardinal = '#c12335';

var colorPrimary = colorAccessibleGreen;

var StyledUnorderedItem = styled.li.withConfig({
  displayName: "UnorderedItem__StyledUnorderedItem",
  componentId: "sc-1sy38gy-0"
})(function (_ref) {
  var iconStyle = _ref.iconStyle,
      size = _ref.size;
  return _extends_1({
    position: 'relative',
    lineHeight: 1
  }, size === 'small' && _extends_1({}, small, {}, smallFont), {}, size === 'medium' && _extends_1({}, medium, {}, mediumFont), {}, size === 'large' && _extends_1({}, large, {}, largeFont), {
    '&::before': _extends_1({
      display: 'block',
      position: 'absolute',
      left: '-2rem',
      fontFamily: 'TELUS Core Icons'
    }, size === 'small' && {
      lineHeight: 1.25
    }, {}, size === 'medium' && {
      lineHeight: 1.6
    }, {}, size === 'large' && {
      lineHeight: 2.1
    }, {}, iconStyle === 'circle' && _extends_1({
      content: "''",
      backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4gIDxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIiBmaWxsPSIjNGIyODZkIiAvPjwvc3ZnPg==)',
      color: colorTelusPurple,
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 100%'
    }, size === 'small' && {
      width: '0.25rem',
      height: '0.25rem',
      top: '0.5rem'
    }, {}, size === 'medium' && {
      width: '0.32rem',
      height: '0.32rem',
      top: '0.65rem'
    }, {}, size === 'large' && {
      width: '0.38rem',
      height: '0.38rem',
      top: '0.87rem'
    }), {}, iconStyle === 'checkmark' && {
      content: "'\f101'",
      fontSize: '1rem',
      width: '0.75rem',
      color: colorPrimary
    }, {}, iconStyle === 'x' && _extends_1({
      content: "'\f104'",
      fontSize: '1rem',
      width: '0.75rem',
      color: colorCardinal
    }, size === 'small' && {
      lineHeight: 1.32
    }))
  });
});

var UnorderedItem = function UnorderedItem(_ref2) {
  var listStyle = _ref2.listStyle,
      itemStyle = _ref2.itemStyle,
      size = _ref2.size,
      children = _ref2.children,
      rest = objectWithoutProperties(_ref2, ["listStyle", "itemStyle", "size", "children"]);

  return React.createElement(StyledUnorderedItem, _extends_1({}, safeRest(rest), {
    iconStyle: itemStyle || listStyle,
    size: size
  }), children);
};

UnorderedItem.propTypes = {
  /**
   * The list's global bullet style. Can be overriden by listStyle.
   * @ignore
   */
  listStyle: PropTypes.oneOf(['circle', 'checkmark', 'x']),

  /**
   * The bullet style of a single item.
   * @since 2.1.0
   */
  itemStyle: PropTypes.oneOf(['circle', 'checkmark', 'x']),

  /**
   * The size of the list items, passed from the parent.
   * @ignore
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  children: PropTypes.node.isRequired
};
UnorderedItem.defaultProps = {
  listStyle: 'circle',
  itemStyle: undefined,
  size: 'medium'
};
UnorderedItem.displayName = 'UnorderedList.Item';

var StyledListContainer = styled(Box)(_extends_1({}, list.nestedListSpacing, {
  '&': {
    paddingLeft: 'calc(2rem + 1px)'
  }
}));

var injectListStyle = function injectListStyle(child, listStyle, size) {
  return React.cloneElement(child, {
    listStyle: listStyle,
    size: size
  });
};
/**
 * @version ./package.json
 */


var UnorderedList = function UnorderedList(_ref) {
  var listStyle = _ref.listStyle,
      size = _ref.size,
      children = _ref.children,
      rest = objectWithoutProperties(_ref, ["listStyle", "size", "children"]);

  var filteredChildren = React.Children.toArray(children).filter(function (child) {
    return child;
  });
  return React.createElement(StyledListContainer, _extends_1({}, safeRest(rest), {
    tag: "ul",
    between: 2
  }), React.Children.map(filteredChildren, function (child) {
    return injectListStyle(child, listStyle, size);
  }));
};

UnorderedList.propTypes = {
  /**
   * The bullet style.
   */
  listStyle: PropTypes.oneOf(['circle', 'checkmark', 'x']),

  /**
   * The size of the list's text.
   *
   * @since 2.0.0
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * The list items. Must be at least one `UnorderedList.Item`.
   */
  children: componentWithName('UnorderedItem').isRequired
};
UnorderedList.defaultProps = {
  listStyle: 'circle',
  size: 'medium'
};
UnorderedList.Item = UnorderedItem;

export default UnorderedList;
